import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

import { COUNTRIES_MAP } from "@/lib/countries";

// Regional-indicator flag emoji are just two Unicode letters offset into a private
// range (🇪 = 'E', 🇸 = 'S' → "ES"), so we can recover the ISO 3166-1 alpha-2 code from
// the flag already sitting in COUNTRIES_MAP instead of hand-maintaining a second
// provider-id → ISO table that could drift the same way the id → name one did.
function flagToIso(flag: string): CountryCode | null {
  const codePoints = Array.from(flag).map((c) => c.codePointAt(0) ?? 0);
  if (codePoints.length < 2) return null;
  const letters = codePoints.slice(0, 2).map((cp) => cp - 0x1f1e6 + 65);
  if (letters.some((n) => n < 65 || n > 90)) return null;
  return letters.map((n) => String.fromCharCode(n)).join("") as CountryCode;
}

const ISO_BY_PROVIDER_COUNTRY: Record<string, CountryCode> = {};
for (const [id, info] of Object.entries(COUNTRIES_MAP)) {
  const iso = flagToIso(info.flag);
  if (iso) ISO_BY_PROVIDER_COUNTRY[id] = iso;
}

/**
 * Hero-SMS hands back numbers as a bare digit string — calling code and national
 * number run together with no "+" and no separators (e.g. "34612345678"). This
 * renders that back out in the target country's own convention: "+34 612 34 56 78".
 */
export function formatPhoneNumber(
  rawNumber: string | null | undefined,
  countryId?: string | number | null,
): string {
  if (!rawNumber) return "";
  const digits = rawNumber.replace(/\D/g, "");
  if (!digits) return rawNumber;

  const iso = countryId != null ? ISO_BY_PROVIDER_COUNTRY[String(countryId).trim()] : undefined;

  if (iso) {
    try {
      const callingCode = getCountryCallingCode(iso);
      if (digits.startsWith(callingCode)) {
        const parsed = parsePhoneNumberFromString(`+${digits}`);
        if (parsed) return parsed.formatInternational();
      }
      // Digits don't carry the expected calling code — still group them under it
      // rather than falling all the way back to an unstructured blob.
      const national = digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits;
      const grouped = national.match(/.{1,3}/g)?.join(" ") ?? national;
      return `+${callingCode} ${grouped}`.trim();
    } catch {
      // Unknown/unsupported ISO in libphonenumber's metadata — fall through.
    }
  }

  return `+${digits.match(/.{1,3}/g)?.join(" ") ?? digits}`;
}
